import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { snappyTween } from './motionUi';

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {(code: string) => Promise<{ characterName: string; bonus: number }>} props.onRedeem
 */
export default function GamePromoModal({ onClose, onRedeem }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setCode('');
    setError('');
    setSuccess(null);
    setBusy(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const norm = code.trim().toUpperCase().replace(/\s+/g, '');
    if (norm.length < 3) {
      setError('Enter a code (3–40 characters).');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const result = await onRedeem(norm);
      setSuccess(result);
    } catch (err) {
      setError(err?.message || 'Could not apply code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5">
      {!success ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm leading-relaxed text-game-body">
            If you have a promo code, enter it here. It will be applied to your account and grant bonus
            relationship points (RP) for the character linked to that code.
          </p>
          <div>
            <label
              htmlFor="game-promo-code"
              className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-game-muted"
            >
              Code
            </label>
            <input
              id="game-promo-code"
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError('');
              }}
              maxLength={40}
              autoComplete="off"
              disabled={busy}
              className="w-full rounded-xl border border-white/15 bg-game-surface px-3 py-2.5 font-mono text-sm uppercase tracking-wide text-game-text placeholder:text-game-muted focus:border-game-accent/50 focus:outline-none focus:ring-1 focus:ring-game-accent/40 disabled:opacity-50"
              placeholder="ABCD123"
            />
            {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
          </div>
          <motion.button
            type="submit"
            disabled={busy}
            transition={snappyTween}
            whileHover={{ scale: busy ? 1 : 1.02 }}
            whileTap={{ scale: busy ? 1 : 0.98 }}
            className="w-full rounded-xl bg-game-accent py-2.5 text-sm font-bold text-game-accentText disabled:opacity-50"
          >
            {busy ? 'Checking…' : 'Apply'}
          </motion.button>
        </form>
      ) : (
        <div className="space-y-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-game-muted">Applied</p>
          <p className="text-sm text-game-body">
            <span className="font-semibold text-game-text">{success.characterName}</span>
            <span className="text-game-muted"> · </span>
            <span className="tabular-nums text-game-accent">+{success.bonus} RP</span>
          </p>
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className="w-full rounded-xl bg-game-accent py-2.5 text-sm font-bold text-game-accentText"
          >
            Done
          </motion.button>
        </div>
      )}
    </div>
  );
}
